import { useEffect, useState } from 'react';
import type { ViewerManifestStatus } from '../types/viewerDeployment';
import type { ViewerRouteContext } from '../utils/viewerRouteContext';
import { fetchViewerRuntimeManifest } from '../Viewer/runtime/api/viewerManifestApi';
import type { ViewerManifestDto } from '../Viewer/runtime/api/types/viewerManifest.api';

interface UseViewerManifestResult {
  manifest: ViewerManifestDto | null;
  status: ViewerManifestStatus;
  error: string | null;
}

export function useViewerManifest(route: ViewerRouteContext): UseViewerManifestResult {
  const [manifest, setManifest] = useState<ViewerManifestDto | null>(null);
  const [status, setStatus] = useState<ViewerManifestStatus>(route.kind === 'unknown' ? 'idle' : 'loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (route.kind === 'unknown') {
      setManifest(null);
      setStatus('idle');
      setError(null);
      return;
    }

    setStatus('loading');
    setError(null);

    fetchViewerRuntimeManifest(route)
      .then((nextManifest) => {
        if (cancelled) return;
        setManifest(nextManifest);
        setStatus('ready');
      })
      .catch((nextError) => {
        if (cancelled) return;
        setManifest(null);
        setStatus('error');
        setError(nextError instanceof Error ? nextError.message : 'Failed to load viewer manifest.');
      });

    return () => {
      cancelled = true;
    };
  }, [route.companyId, route.isCanonical, route.kind, route.previewToken, route.productId]);

  return { manifest, status, error };
}
